import { useState } from "react";
import { ChevronDown, ChevronRight, FileText, Info, Plus, Search, Sparkles, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Checkbox } from "@/components/ui/checkbox";

export const MAX_WAVES = 6;
export const MAX_WAVE_INSTRUCTIONS = 2000;
const MIN_DELAY_HOURS = 1;
const MAX_DELAY_HOURS = 720;

export type WaveContentMode = "ai" | "template";

export interface FollowUpWave {
  id: string;
  delayHours: number;
  contentMode: WaveContentMode;
  instructions: string;
  sourceId: string | null;
}

export interface WaveSource {
  id: string;
  name: string;
  type: string;
  status: string;
  content: string;
}

export const MAX_EXTRA_STOP_TEXT = 500;

/** What the server says about each wave once it's live
 *  (supabase/functions/_shared/workflows/followUpWaves.ts). */
export interface WaveLiveStatus {
  id: string;
  contentMode: WaveContentMode;
  /** Why the wave is limited, in the owner's words. */
  issue?: string;
  /** Templated waves: the WhatsApp template's review by Meta. */
  whatsapp?: { status: string; note: string | null };
}

export interface FollowUpLiveStatus {
  waves: WaveLiveStatus[];
  /** Why the waves can't go live at all. */
  issues: string[];
}

/** A templated wave's WhatsApp state as a short label and a dot colour. */
const whatsappLabel = (whatsapp: WaveLiveStatus["whatsapp"]) => {
  if (!whatsapp) return null;
  switch (whatsapp.status) {
    case "approved":
      return { text: "Approved by WhatsApp", dot: "bg-emerald-500" };
    case "submitted":
      return { text: "Waiting for WhatsApp approval", dot: "bg-amber-500" };
    case "rejected":
      return { text: "Rejected by WhatsApp", dot: "bg-destructive" };
    case "paused":
      return { text: "Paused by WhatsApp", dot: "bg-destructive" };
    default:
      return { text: "Not sent to WhatsApp yet", dot: "bg-muted-foreground/50" };
  }
};

export interface StopTriggers {
  stopOnBooking: boolean;
  stopOnOptOut: boolean;
  stopOnStaffReply: boolean;
  handoffAfterLastWave: boolean;
  extraStopText: string;
}

export const defaultStopTriggers = (): StopTriggers => ({
  stopOnBooking: true,
  stopOnOptOut: true,
  stopOnStaffReply: true,
  handoffAfterLastWave: false,
  extraStopText: "",
});

export const triggersFromServer = (raw: unknown): StopTriggers => {
  const defaults = defaultStopTriggers();
  if (!raw || typeof raw !== "object") return defaults;
  const row = raw as Record<string, unknown>;
  const flag = (key: string, fallback: boolean) => (typeof row[key] === "boolean" ? (row[key] as boolean) : fallback);
  return {
    stopOnBooking: flag("stop_on_booking", defaults.stopOnBooking),
    stopOnOptOut: flag("stop_on_opt_out", defaults.stopOnOptOut),
    stopOnStaffReply: flag("stop_on_staff_reply", defaults.stopOnStaffReply),
    handoffAfterLastWave: flag("handoff_after_last_wave", defaults.handoffAfterLastWave),
    extraStopText: typeof row.extra_stop_text === "string" ? row.extra_stop_text.slice(0, MAX_EXTRA_STOP_TEXT) : "",
  };
};

export const triggersToServer = (triggers: StopTriggers) => ({
  stop_on_booking: triggers.stopOnBooking,
  stop_on_opt_out: triggers.stopOnOptOut,
  stop_on_staff_reply: triggers.stopOnStaffReply,
  handoff_after_last_wave: triggers.handoffAfterLastWave,
  extra_stop_text: triggers.extraStopText.trim().slice(0, MAX_EXTRA_STOP_TEXT),
});

export const newWave = (delayHours = 24): FollowUpWave => ({
  id: crypto.randomUUID(),
  delayHours,
  contentMode: "ai",
  instructions: "",
  sourceId: null,
});

export const wavesFromServer = (raw: unknown): FollowUpWave[] => {
  if (!Array.isArray(raw)) return [];
  return raw.slice(0, MAX_WAVES).map((item) => {
    const wave = (item ?? {}) as Record<string, unknown>;
    return {
      id: typeof wave.id === "string" ? wave.id : crypto.randomUUID(),
      delayHours: clampDelay(Number(wave.delay_hours)),
      contentMode: wave.content_mode === "template" ? "template" : "ai",
      instructions: typeof wave.instructions === "string" ? wave.instructions : "",
      sourceId: typeof wave.source_id === "string" ? wave.source_id : null,
    };
  });
};

export const wavesToServer = (waves: FollowUpWave[]) =>
  waves.slice(0, MAX_WAVES).map((wave) => ({
    id: wave.id,
    delay_hours: clampDelay(wave.delayHours),
    content_mode: wave.contentMode,
    instructions: wave.instructions.trim().slice(0, MAX_WAVE_INSTRUCTIONS),
    source_id: wave.sourceId,
  }));

function clampDelay(value: number) {
  if (!Number.isFinite(value)) return 24;
  return Math.min(MAX_DELAY_HOURS, Math.max(MIN_DELAY_HOURS, Math.round(value)));
}

export interface WaveSourcePickerProps {
  waveId: string;
  label: string;
  hint: string;
  sources: WaveSource[];
  loading: boolean;
  selectedId: string | null;
  disabled: boolean;
  /** Wording for pickers that choose something other than a generic source, e.g. an offer. */
  placeholder?: string;
  popoverTitle?: string;
  emptyText?: string;
  onChange: (sourceId: string | null) => void;
}

export const WaveSourcePicker = ({
  waveId,
  label,
  hint,
  sources,
  loading,
  selectedId,
  disabled,
  placeholder = "Choose a source",
  popoverTitle = "Knowledge database",
  emptyText = "No notes, FAQs or Markdown files are available yet.",
  onChange,
}: WaveSourcePickerProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const normalized = query.trim().toLocaleLowerCase();
  const visible = sources.filter((source) =>
    !normalized || [source.name, source.content].join(" ").toLocaleLowerCase().includes(normalized),
  );
  const selected = selectedId
    ? sources.find((source) => source.id === selectedId) ?? {
      id: selectedId,
      name: loading ? "Loading…" : "Removed from Knowledge",
    }
    : null;
  const labelId = `wave-${waveId}-source-label`;

  const choose = (sourceId: string | null) => {
    onChange(sourceId);
    setOpen(false);
  };

  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <p id={labelId} className="text-xs font-medium text-foreground">{label}</p>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-labelledby={labelId}
            disabled={disabled}
            className="mt-1.5 flex h-9 w-full items-center gap-2 rounded-md border border-input bg-card px-2.5 text-left text-xs transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          >
            <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className={`min-w-0 flex-1 truncate ${selected ? "text-foreground" : "text-muted-foreground"}`}>
              {selected ? selected.name : placeholder}
            </span>
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <p className="mt-1.5 text-xs leading-5 text-muted-foreground">{hint}</p>

        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] p-2"
          onPointerDown={(event) => event.stopPropagation()}
          onWheel={(event) => event.stopPropagation()}
        >
          <p className="px-2 pb-1 pt-1 text-xs font-semibold text-foreground">{popoverTitle}</p>
          <div className="relative my-1.5">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search knowledge..."
              className="h-8 pl-8 text-xs focus-visible:ring-0 focus-visible:ring-offset-0"
              aria-label="Search knowledge"
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <p className="px-2 py-3 text-xs text-muted-foreground">Loading sources…</p>
            ) : visible.length === 0 ? (
              <p className="px-2 py-3 text-xs leading-5 text-muted-foreground">
                {sources.length === 0
                  ? emptyText
                  : "Nothing matches your search."}
              </p>
            ) : (
              <div role="radiogroup" aria-label={label}>
                {selected && (
                  <div
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-xs text-muted-foreground transition-colors hover:bg-muted"
                    role="radio"
                    tabIndex={0}
                    aria-checked={false}
                    onClick={() => choose(null)}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter" && event.key !== " ") return;
                      event.preventDefault();
                      choose(null);
                    }}
                  >
                    <X className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    No source
                  </div>
                )}
                {visible.map((source) => {
                  const isSelected = source.id === selectedId;
                  return (
                    <div
                      key={source.id}
                      className={`flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 transition-colors hover:bg-muted ${isSelected ? "bg-muted/60" : ""}`}
                      role="radio"
                      tabIndex={0}
                      aria-checked={isSelected}
                      onClick={() => choose(source.id)}
                      onKeyDown={(event) => {
                        if (event.key !== "Enter" && event.key !== " ") return;
                        event.preventDefault();
                        choose(source.id);
                      }}
                    >
                      <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-xs font-medium text-foreground">{source.name}</span>
                        <span className="block text-[11px] text-muted-foreground">
                          {source.type === "faq" ? "FAQ" : source.type === "file" ? "File" : source.type === "offer" ? "Offer" : "Note"} · {source.status}
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

const InfoTip = ({ label, children }: { label: string; children: string }) => (
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className="rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[220px] text-xs">{children}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

interface TriggerRowProps {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  disabled: boolean;
  onChange?: (checked: boolean) => void;
}

const TriggerRow = ({ id, label, hint, checked, disabled, onChange }: TriggerRowProps) => (
  <div className="flex items-center gap-3">
    <Checkbox
      id={id}
      checked={checked}
      disabled={disabled}
      className="border-foreground data-[state=checked]:bg-foreground data-[state=checked]:text-background"
      onCheckedChange={(value) => onChange?.(value === true)}
    />
    <label htmlFor={id} className={`text-xs font-medium text-foreground ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}>
      {label}
    </label>
    <InfoTip label={`About ${label.toLocaleLowerCase()}`}>{hint}</InfoTip>
  </div>
);

interface FollowUpWavesEditorProps {
  waves: FollowUpWave[];
  sources: WaveSource[];
  sourcesLoading: boolean;
  disabled: boolean;
  triggers: StopTriggers;
  /** Called with the next list whenever a change should be saved. */
  onCommit: (waves: FollowUpWave[]) => void;
  onTriggersCommit: (triggers: StopTriggers) => void;
  /** The live state of each saved wave, when the server has reported it. */
  liveStatus?: FollowUpLiveStatus | null;
}

const FollowUpWavesEditor = ({ waves, sources, sourcesLoading, disabled, triggers, onCommit, onTriggersCommit, liveStatus }: FollowUpWavesEditorProps) => {
  // Every wave starts collapsed; a wave you just added opens so you can fill it in.
  const [openIds, setOpenIds] = useState<Set<string>>(() => new Set());
  const toggleOpen = (id: string) =>
    setOpenIds((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  const [extraStopDraft, setExtraStopDraft] = useState<string | null>(null);
  // Text fields edit a local draft and commit on blur, so each keystroke isn't a save.
  const [drafts, setDrafts] = useState<Record<string, { delay?: string; instructions?: string }>>({});

  const update = (id: string, patch: Partial<FollowUpWave>) =>
    onCommit(waves.map((wave) => (wave.id === id ? { ...wave, ...patch } : wave)));

  const setDraft = (id: string, patch: { delay?: string; instructions?: string }) =>
    setDrafts((current) => ({ ...current, [id]: { ...current[id], ...patch } }));

  const clearDraft = (id: string, key: "delay" | "instructions") =>
    setDrafts((current) => {
      const { [key]: _removed, ...rest } = current[id] ?? {};
      return { ...current, [id]: rest };
    });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1.5">
        <h3 className="text-sm font-semibold text-foreground">Waves</h3>
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="About waves"
                className="rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Info className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[220px] text-xs">
              Each wave is one follow-up message. Dibs sends the next wave only if the lead still hasn&apos;t replied.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="space-y-2">
      {waves.map((wave, index) => {
        const delayValue = drafts[wave.id]?.delay ?? String(wave.delayHours);
        const instructionsValue = drafts[wave.id]?.instructions ?? wave.instructions;
        const delayId = `wave-${wave.id}-delay`;
        const instructionsId = `wave-${wave.id}-instructions`;
        const bodyId = `wave-${wave.id}-body`;
        const expanded = openIds.has(wave.id);
        // Only trust a status reported for this wave as it is now.
        const live = liveStatus?.waves.find((item) => item.id === wave.id && item.contentMode === wave.contentMode);
        const whatsapp = wave.contentMode === "template" ? whatsappLabel(live?.whatsapp) : null;
        return (
          <section
            key={wave.id}
            aria-label={`Wave ${index + 1}`}
            className="rounded-md border border-border bg-card"
          >
            <div className={`flex items-center gap-1 pr-2 transition-colors hover:bg-secondary ${expanded ? "rounded-t-md" : "rounded-md"}`}>
              <button
                type="button"
                aria-expanded={expanded}
                aria-controls={bodyId}
                onClick={() => toggleOpen(wave.id)}
                className="flex min-w-0 flex-1 items-center gap-2.5 rounded-md px-3 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <ChevronRight
                  className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ${expanded ? "rotate-90" : ""}`}
                  aria-hidden="true"
                />
                <span
                  aria-hidden="true"
                  className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-muted-foreground"
                >
                  {index + 1}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-foreground">Wave {index + 1}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    After {wave.delayHours} {wave.delayHours === 1 ? "hour" : "hours"} ·{" "}
                    {wave.contentMode === "ai" ? "AI-generated" : "Templated"}
                  </span>
                  {whatsapp && (
                    <span className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${whatsapp.dot}`} aria-hidden="true" />
                      {whatsapp.text}
                    </span>
                  )}
                </span>
              </button>
              {waves.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={disabled}
                  aria-label={`Remove wave ${index + 1}`}
                  className="h-7 w-7 shrink-0 text-muted-foreground"
                  onClick={() => {
                    setDrafts(({ [wave.id]: _removed, ...rest }) => rest);
                    onCommit(waves.filter((item) => item.id !== wave.id));
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>

            {expanded && (
            <div id={bodyId} className="border-t border-border px-4 pb-4">
            <div className="mt-4">
              <div className="flex items-center gap-1.5">
                <label htmlFor={delayId} className="text-sm font-semibold text-foreground">Timing</label>
                <InfoTip label="About timing">
                  {index === 0
                    ? "Dibs sends this wave once the lead has gone this long without replying."
                    : `Dibs sends this wave this long after wave ${index} if the lead still hasn't replied.`}
                </InfoTip>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Input
                  id={delayId}
                  type="number"
                  inputMode="numeric"
                  min={MIN_DELAY_HOURS}
                  max={MAX_DELAY_HOURS}
                  value={delayValue}
                  disabled={disabled}
                  className="h-8 w-20 text-center text-xs [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  onChange={(event) => setDraft(wave.id, { delay: event.target.value })}
                  onBlur={() => {
                    const next = clampDelay(Number(delayValue));
                    clearDraft(wave.id, "delay");
                    if (next !== wave.delayHours) update(wave.id, { delayHours: next });
                  }}
                />
                <span className="text-xs text-muted-foreground">
                  {wave.delayHours === 1 ? "hour" : "hours"} without a reply from the lead
                </span>
              </div>
            </div>

            <div className="mt-5">
              <p id={`wave-${wave.id}-content-label`} className="text-xs font-medium text-foreground">Message content</p>
              <RadioGroup
                className="mt-2 gap-2"
                value={wave.contentMode}
                disabled={disabled}
                onValueChange={(value) => update(wave.id, {
                  contentMode: value as WaveContentMode,
                  // Only templated waves use a source.
                  ...(value === "ai" ? { sourceId: null } : {}),
                })}
                aria-labelledby={`wave-${wave.id}-content-label`}
              >
                <label
                  className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors ${
                    wave.contentMode === "ai" ? "border-foreground/35 bg-muted/60" : "border-border hover:bg-muted/40"
                  }`}
                >
                  <RadioGroupItem value="ai" aria-label="AI-generated" className="mt-0.5 border-foreground text-foreground" />
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                      <Sparkles className="h-3.5 w-3.5 text-primary" />
                      AI-generated
                    </span>
                    <span className="mt-1 block text-xs leading-4 text-muted-foreground">
                      Dibs writes this follow-up using customer and knowledge context.
                    </span>
                  </span>
                </label>
                <label
                  className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors ${
                    wave.contentMode === "template" ? "border-foreground/35 bg-muted/60" : "border-border hover:bg-muted/40"
                  }`}
                >
                  <RadioGroupItem value="template" aria-label="Templated" className="mt-0.5 border-foreground text-foreground" />
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                      <FileText className="h-3.5 w-3.5" />
                      Templated
                    </span>
                    <span className="mt-1 block text-xs leading-4 text-muted-foreground">
                      Send the same pre-written follow-up to every lead.
                    </span>
                  </span>
                </label>
              </RadioGroup>
            </div>

            {wave.contentMode === "ai" && (
              <div className="mt-4">
                <p className="mb-3 text-xs leading-5 text-muted-foreground">
                  On WhatsApp, an AI-written follow-up only goes out within 24 hours of the lead&apos;s last message. Choose Templated to reach WhatsApp leads after that.
                </p>
                <label htmlFor={instructionsId} className="text-xs font-medium text-foreground">
                  Instructions for message generated
                </label>
                <Textarea
                  id={instructionsId}
                  value={instructionsValue}
                  maxLength={MAX_WAVE_INSTRUCTIONS}
                  disabled={disabled}
                  placeholder="e.g. Keep it to one line and mention the free trial."
                  className="mt-1.5 min-h-[96px] text-xs"
                  onChange={(event) => setDraft(wave.id, { instructions: event.target.value })}
                  onBlur={() => {
                    const next = instructionsValue.trim();
                    clearDraft(wave.id, "instructions");
                    if (next !== wave.instructions.trim()) update(wave.id, { instructions: next });
                  }}
                />
              </div>
            )}

            {wave.contentMode === "template" && (
              <div className="mt-4">
                <WaveSourcePicker
                  waveId={wave.id}
                  label="Source"
                  hint="Knowledge used for templated follow-ups."
                  sources={sources}
                  loading={sourcesLoading}
                  selectedId={wave.sourceId}
                  disabled={disabled}
                  onChange={(sourceId) => update(wave.id, { sourceId })}
                />
                {live?.issue ? (
                  <p role="status" className="mt-2 text-xs leading-5 text-amber-700 dark:text-amber-400">{live.issue}</p>
                ) : live?.whatsapp ? (
                  live.whatsapp.note && <p role="status" className="mt-2 text-xs leading-5 text-muted-foreground">{live.whatsapp.note}</p>
                ) : wave.sourceId ? (
                  <p className="mt-2 text-xs leading-5 text-muted-foreground">
                    Dibs sends this text to WhatsApp for approval, so it can reach leads after 24 hours.
                  </p>
                ) : null}
              </div>
            )}
            </div>
            )}
          </section>
        );
      })}
      </div>

      {liveStatus && liveStatus.issues.length > 0 && (
        <div role="alert" className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-950 dark:border-amber-900/70 dark:bg-amber-950/40 dark:text-amber-100">
          {liveStatus.issues.map((issue) => <p key={issue}>{issue}</p>)}
        </div>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled || waves.length >= MAX_WAVES}
        className="standard-action-button neutral-action-button w-full gap-1.5"
        onClick={() => {
          const added = newWave(24);
          setOpenIds((current) => new Set(current).add(added.id));
          onCommit([...waves, added]);
        }}
      >
        <Plus className="h-3.5 w-3.5" />
        Add wave
      </Button>

      <section aria-labelledby="stop-triggers-heading" className="pt-4">
        <h3 id="stop-triggers-heading" className="text-sm font-semibold text-foreground">Stop following up after</h3>
        <div className="mt-4 space-y-3">
          <TriggerRow
            id="stop-on-reply"
            label="Lead replies"
            hint="Always on. Dibs never sends another wave once the lead replies."
            checked
            disabled
          />
          {([
            ["stopOnBooking", "The lead books", "Nothing more to chase once they've booked."],
            ["stopOnOptOut", "The lead asks to stop", "Includes \u201Cnot interested\u201D and \u201Cstop messaging me\u201D."],
            ["stopOnStaffReply", "Your team replies", "Staff took over the chat, so Dibs stays out of it."],
          ] as const).map(([key, label, hint]) => (
            <TriggerRow
              key={key}
              id={`stop-${key}`}
              label={label}
              hint={hint}
              checked={triggers[key]}
              disabled={disabled}
              onChange={(checked) => onTriggersCommit({ ...triggers, [key]: checked })}
            />
          ))}
        </div>

        <div className="mt-4">
          <label htmlFor="stop-extra-text" className="text-xs font-medium text-foreground">Also stop if (optional)</label>
          <Textarea
            id="stop-extra-text"
            value={extraStopDraft ?? triggers.extraStopText}
            maxLength={MAX_EXTRA_STOP_TEXT}
            disabled={disabled}
            placeholder="e.g. The lead says they've already booked elsewhere."
            className="mt-1.5 min-h-[72px] text-xs"
            onChange={(event) => setExtraStopDraft(event.target.value)}
            onBlur={() => {
              const next = (extraStopDraft ?? triggers.extraStopText).trim();
              setExtraStopDraft(null);
              if (next !== triggers.extraStopText.trim()) onTriggersCommit({ ...triggers, extraStopText: next });
            }}
          />
        </div>

        <h4 className="mt-6 text-sm font-semibold text-foreground">Hand over to staff when</h4>
        <div className="mt-2">
          <TriggerRow
            id="handoff-after-last-wave"
            label="The last wave is sent with no reply"
            hint={`Wave ${waves.length} goes out and the lead still hasn't replied. Dibs flags the conversation so your team can follow up personally.`}
            checked={triggers.handoffAfterLastWave}
            disabled={disabled}
            onChange={(checked) => onTriggersCommit({ ...triggers, handoffAfterLastWave: checked })}
          />
        </div>
      </section>
    </div>
  );
};

export default FollowUpWavesEditor;
